import React, { useEffect, useState } from 'react';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { Stack, Button, Row, Col, Image } from 'react-bootstrap';
import {
	useCancelSeekerMeetingMutation,
	useGetSeekerMeetingListQuery,
	useSaveSeekerAvailabilityMutation,
} from 'services';
import useAuthStore from 'store/auth';
import useCommonStore, { setModalConfig } from 'store/common';
import PaginationBox from 'components/PaginationBox';
import { dataLimits, S3_URL } from 'constants';
import CopyButton from 'components/Common/CopyButton';
import moment from 'moment';
import useTranslate from 'hooks/useTranslate';
import toast from 'react-hot-toast';
import { isTimeGone } from 'utils';
import { useNavigate } from 'react-router-dom';
import './styles.scss';

const ScheduledMeetings: React.FC = () => {
	const navigate = useNavigate();
	const { translate } = useTranslate();
	const [currentPage, setCurrentPage] = useState(1);
	const { hideCommonModal }: any = useCommonStore((state) => state);
	const payload: any = {
		page: currentPage,
		limit: dataLimits.limits,
	};
	const { mutateAsync: saveSeekerAvailabilityMutation } = useSaveSeekerAvailabilityMutation();
	const {
		data: { docs: listData, meta: { totalDocs = 0, limit = 0 } = {} } = [],
		refetch: refetchList,
	} = useGetSeekerMeetingListQuery(payload);
	const { mutateAsync: cancelMeetingMutation } = useCancelSeekerMeetingMutation();
	const { user }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});

	useEffect(() => {
		refetchList();
	}, [currentPage]);

	const handleShow = () => {
		setModalConfig({
			visible: true,
			id: user?.userId,
			type: 'meetingScheduleModal',
			onClick: saveAvailability,
			props: {
				dialogClassName: 'schedule-meeting-dialog',
			},
		});
	};

	const saveAvailability = (timeSlots: [string], date: string) => {
		saveSeekerAvailabilityMutation({ timeSlots, date }).finally(() => {
			hideCommonModal();
			toast.success(translate('ToastMessage.meetingScheduled'));
		});
	};

	const cancelMeeting = (meetingId: any) => {
		cancelMeetingMutation({ meetingId })
			.then((res: any) => {
				if (res?.success) {
					refetchList();
					toast.success(translate('ToastMessage.meetingCancelled'));
				}
			})
			.finally(() => {
				hideCommonModal();
			});
	};

	const handleCancelMeetingModal = (meetingId: number) => {
		setModalConfig({
			visible: true,
			type: 'confirmModal',
			onClick: () => cancelMeeting(meetingId),
			data: {
				title: translate('Modal.wantToCancelMeeting'),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	// Helper function to generate meeting button
	const renderMeetingButton = (res: any) => {
		const isMeetingToday =
			moment().format('MM/DD/YYYY') === moment(res?.interviewDate).format('MM/DD/YYYY');
		const meetingIsInPast = moment(res?.interviewDate).isBefore(moment(), 'day');
		const isCompleted = res?.status === 'completed';
		const isTimePassed = isTimeGone(res?.interviewTime);

		// Check if the Cancel Meeting button is visible
		const isCancelButtonVisible =
			!isCompleted && ((!meetingIsInPast && !isMeetingToday) || (isMeetingToday && !isTimePassed));

		return (
			<>
				{/* Show meeting link and CopyButton only if Cancel button is visible */}
				<div
					className="d-flex align-items-center gap-2"
					style={{
						visibility: isCancelButtonVisible ? 'visible' : 'hidden',
					}}
				>
					<div className="meeting-link rounded-pill flex-1">
						<h6 className="mb-0">{res?.interviewLink}</h6>
					</div>
					<CopyButton text={res?.interviewLink} />
				</div>

				{isCancelButtonVisible && (
					<Button
						onClick={() => handleCancelMeetingModal(res?.id)}
						className={`${isCompleted ? '' : 'gray-transparent-btn'} rounded-pill lh-1 mt-auto`}
						disabled={isCompleted}
					>
						{isCompleted
							? translate('Common.markAsDoneByCompany')
							: translate('Common.cancelMeeting')}
					</Button>
				)}

				{/* Show no data if the time is passed and it's the same day */}
				{(meetingIsInPast || (isMeetingToday && isTimePassed)) && (
					<div className="meeting-link rounded-pill flex-1 text-center d-flex justify-content-center align-items-center">
						<h6 className="mb-0 fw-bold">{translate('Common.markAsDoneByCompany')}</h6>
					</div>
				)}
			</>
		);
	};

	const goToJobDescriptionPage = (jobId: any) => {
		navigate(`/job-detail/${jobId}`);
	};

	return (
		<div className="right-area flex-1 d-flex flex-column">
			<div className="white-box bg-white">
				<Stack className="gap-3 gap-md-4">
					<div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
						<div className="list-heading">
							<h4 className="fw-bold">{translate('Common.scheduledMeetings')}</h4>
							{listData?.length > 0 && (
								<p className="mb-0">
									{translate('Common.showingResults', { noOfResults: listData?.length.toString() })}
								</p>
							)}
						</div>
						<Button className="filled-btn rounded-pill lh-1 width-fit-content" onClick={handleShow}>
							{translate('Common.setMeetingAvailability')}
						</Button>
					</div>

					<Row className="scheduled-meetings">
						{listData?.length ? (
							listData.map((res: any, index: number) => (
								<Col
									key={`${res?.jobId}-${index}-${res?.companyId}-${res?.seekerId}`}
									xxl={4}
									lg={6}
								>
									<Stack className="scheduled-meetings-box gap-3">
										<div className="d-flex gap-2 gap-md-3">
											<div className="scheduled-meetings-box-logo">
												<Image
													src={`${S3_URL}${res?.profile}`}
													onError={({ currentTarget }) => {
														currentTarget.onerror = null;
														currentTarget.src = IMAGE_PATHS.JobLogo1;
													}}
													style={{ cursor: 'pointer' }}
													onClick={() => goToJobDescriptionPage(res?.jobId)}
													alt="logo"
													className="w-100 h-100 object-fit-cover"
													loading="lazy"
												/>
											</div>
											<div>
												<h5
													style={{ cursor: 'pointer' }}
													onClick={() => goToJobDescriptionPage(res?.jobId)}
													className="fw-bold"
												>
													{res?.companyName}
												</h5>
												<h6 className="mb-0">{res?.title}</h6>
											</div>
										</div>
										<div className="date-time d-flex flex-wrap align-items-center gap-3">
											<div className="d-flex align-items-center gap-2">
												<i className="ri-calendar-line"></i>
												<p className="mb-0">{moment(res?.interviewDate).format('D MMM, YYYY')}</p>
											</div>
											<div className="d-flex align-items-center gap-2">
												<i className="ri-time-line"></i>
												<p className="mb-0">{res?.interviewTime}</p>
											</div>
										</div>

										{renderMeetingButton(res)}
									</Stack>
								</Col>
							))
						) : (
							<Stack>
								<p className="mb-0">{translate('Common.noDataFound')}</p>
							</Stack>
						)}
					</Row>

					{totalDocs > limit && (
						<PaginationBox
							totalData={totalDocs}
							limit={limit}
							page={currentPage}
							setCurrentPage={setCurrentPage}
						/>
					)}
				</Stack>
			</div>
		</div>
	);
};

export default ScheduledMeetings;
